/**
 * "Edit plan" (Breakdowns tab): the top of the breakdown, editable, with the
 * tab's sections and labels.
 *
 * Member | Patient sit side by side as compact rows, read-only until a row's
 * pencil is clicked; that one value then edits in place and saves on its own
 * (one PATCH of that field). The patient's own details come from the PMS and
 * have no pencil; the relationship is the policy's, so it does.
 *
 * Plan details and Coverage conditions each take a full row, three columns, a
 * label over each field. They are filled in together and saved with the
 * footer's Save: only the fields that changed are sent (one PATCH), and only
 * those are locked against the next scrape (the server locks what changed).
 *
 * Where the tab shows a value worked out from the breakdown's lines and nothing
 * is stored (planFieldHints), the field's label line says so — "Tab shows Per
 * person" — with Use where those words are the stored answer.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Input, Select, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { controller as rootController } from "../../../../../controller";
import { describeRepoint } from "../../../lib/carrierLabel";
import { PLAN_DETAIL_ROWS } from "../../../lib/planDetails";
import MemberDobSuggestion from "../../shared/MemberDobSuggestion";
import { memberFields, patientFields } from "./PlanSummary";
import {
  autoFillSource, benefitYearOf, coverageConditionGroups, fromLinesLabels, molarFillingsOf, planDetailFields, planFieldHints,
} from "./sheetModel";
import "./breakdownSheet.css";

const ORDINALS = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
  { value: "tertiary", label: "Tertiary" },
  { value: "other", label: "Other" },
];
const RELATIONS = [
  { value: "self", label: "Self" },
  { value: "spouse", label: "Spouse" },
  { value: "child", label: "Child" },
  { value: "other", label: "Other" },
];
const SPECIALIST = [{ value: true, label: "Covered" }, { value: false, label: "Not covered" }];
const PERIO_ENDO = [{ value: "yes", label: "In Basic" }, { value: "no", label: "Not in Basic" }, { value: "unknown", label: "Unknown" }];

// The member's and patient's values, each saved on its own from its row.
const IDENTITY = {
  first_name: "text", last_name: "text", date_of_birth: "date", member_id: "text",
  insurance_name: "text", insurance_type: "text", contract_number: "text", plan_name: "text",
  relation_to_member: "text",
};
// Plan details and coverage conditions, saved together by the footer's Save.
const FIELDS = {
  benefit_period_type: "text", benefit_year_start: "date", benefit_year_end: "date",
  deductible: "text", fee_guide: "text", max_scope: "text", assignment_of_benefits: "text",
  age_limit: "integer", coverage_end_date: "date",
  alternate_benefit: "text", alternate_benefit_note: "text", waiting_period_note: "text",
  specialist_fee_covered: "boolean", specialist_fee_scope: "text", missing_tooth_clause: "text",
  implants_coverage: "text", implants_percentage: "decimal", perio_endo_in_basic: "text",
  predetermination_threshold: "decimal", coverage_scope: "text",
  dependent_age_limit: "integer", student_age_limit: "integer",
};

// The Member rows, in the tab's order (memberFields' labels), and the policy
// fields each one edits.
const MEMBER_ROWS = [
  { label: "Name", fields: ["first_name", "last_name"] },
  { label: "Date of birth", fields: ["date_of_birth"] },
  { label: "Carrier", fields: ["insurance_name"] },
  { label: "Plan name", fields: ["plan_name"] },
  { label: "Ordinal", fields: ["insurance_type"] },
  { label: "Group", fields: ["contract_number"] },
  { label: "Member ID", fields: ["member_id"] },
];
const RELATIONSHIP_ROW = { label: "Relationship", fields: ["relation_to_member"] };

function optionsOf(field) {
  const row = PLAN_DETAIL_ROWS.find((r) => r.field === field);
  return row ? row.options : [];
}

/** A value as it is sent: "" and blanks are null, numbers typed, dates ISO. */
export function sendValue(kind, v) {
  if (v == null || v === "") return null;
  if (kind === "date") {
    const d = dayjs.isDayjs(v) ? v : dayjs(String(v).split("T")[0]);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  }
  if (kind === "integer") {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? null : n;
  }
  if (kind === "decimal") return String(v).trim() === "" ? null : String(Number(v));
  if (kind === "boolean") return v === true || v === false ? v : null;
  const t = String(v).trim();
  return t === "" ? null : t;
}

/**
 * The plan details and coverage conditions that changed, ready to PATCH. The
 * benefit year goes as a pair.
 */
export function changedFields(original, draft) {
  const out = {};
  Object.keys(FIELDS).forEach((f) => {
    const before = sendValue(FIELDS[f], (original || {})[f]);
    const after = sendValue(FIELDS[f], (draft || {})[f]);
    if (before !== after) out[f] = after;
  });
  if ("benefit_year_start" in out || "benefit_year_end" in out) {
    out.benefit_year_start = sendValue("date", draft.benefit_year_start);
    out.benefit_year_end = sendValue("date", draft.benefit_year_end);
  }
  return out;
}

/** The plan details and coverage conditions of `policy`, as a draft to edit. */
export function planDraftOf(policy) {
  return valuesOf(policy, FIELDS);
}

function valuesOf(policy, kinds) {
  const p = policy || {};
  const d = {};
  Object.keys(kinds).forEach((f) => { d[f] = p[f] == null ? "" : p[f]; });
  return d;
}

function asDay(v) {
  if (!v) return null;
  const d = dayjs.isDayjs(v) ? v : dayjs(String(v).split("T")[0]);
  return d.isValid() ? d : null;
}

// A suggested answer for an empty field, on the label's line: what the tab
// shows ("Tab shows Per person"), or `hint.label` (another member's plan).
function Suggestion({ hint, onUse }) {
  if (!hint) return null;
  const words = hint.label || `Tab shows ${hint.text}`;
  const use = hint.onUse || (hint.value != null && onUse ? () => onUse(hint.value) : null);
  return (
    <span className="pe-sugg">
      <span className="pe-sugg-text" title={hint.label || `The tab shows ${hint.text}`}>{words}</span>
      {use ? <button type="button" className="bs-link" onClick={use}>Use</button> : null}
    </span>
  );
}

// A label over its field. `done`: an answered field shown as its words, with
// Change to open the field (the wizard's "only what's missing").
function Field({ label, htmlFor, hint, onUse, note, done, onOpen, children }) {
  if (done) {
    return (
      <div className="pe-field done">
        <div className="pe-label">
          <span className="pe-lbl">{label}</span>
          <button type="button" className="bs-link pe-change" onClick={onOpen}>Change</button>
        </div>
        <div className="pe-static">{done}</div>
        {note ? <div className="pe-note">{note}</div> : null}
      </div>
    );
  }
  return (
    <div className="pe-field">
      <div className="pe-label">
        <label className="pe-lbl" htmlFor={htmlFor}>{label}</label>
        <Suggestion hint={hint} onUse={onUse} />
      </div>
      {children}
      {note ? <div className="pe-note">{note}</div> : null}
    </div>
  );
}

// One compact row: label | value, and a pencil when it can be changed. While
// editing, the value is its field with Save and Cancel beside it.
function QuickRow({ label, value, editable, editing, editor, extra, saving, onEdit, onSave, onCancel }) {
  return (
    <div className={editing ? "pe-q editing" : "pe-q"}>
      <dt>{label}</dt>
      <dd>
        {editing ? (
          <div className="pe-q-edit"
            onKeyDown={(e) => { if (e.key === "Escape" && !e.defaultPrevented) { e.stopPropagation(); onCancel(); } }}>
            {editor}
            <Button size="small" type="primary" onClick={onSave} loading={saving}>Save</Button>
            <Button size="small" onClick={onCancel} disabled={saving}>Cancel</Button>
            {extra ? <div className="pe-q-more">{extra}</div> : null}
          </div>
        ) : (
          <>
            <span className={value == null ? "pe-q-val none" : "pe-q-val"} title={value || undefined}>{value == null ? "—" : value}</span>
            {editable ? (
              <button type="button" className="pe-q-pen" aria-label={`Edit ${label.toLowerCase()}`} title={`Edit ${label.toLowerCase()}`} onClick={onEdit}>
                <EditOutlined />
              </button>
            ) : null}
          </>
        )}
      </dd>
    </div>
  );
}

/**
 * Plan details and Coverage conditions, as fields (the Edit plan panel, and
 * step 2 of the Add Manually wizard). `onChange(field, value)` edits the
 * caller's draft. `collapseAnswered`: a field the plan already answers shows
 * as its words with Change, so only what's missing is asked. `extraHints`:
 * suggested answers by field ({label, value | onUse}) beside the tab's own.
 */
export function PlanFieldsForm({ policy, rows, draft, onChange, maxResetDate, extraHints, collapseAnswered = false }) {
  const [opened, setOpened] = useState({});
  const answered = useMemo(() => (rows || []).filter((r) => r && !r.data_unavailable), [rows]);
  const hints = useMemo(() => planFieldHints({ policy, rows: answered }), [policy, answered]);
  const words = useMemo(() => {
    const out = {};
    if (!collapseAnswered) return out;
    planDetailFields({ policy, rows: answered, maxResetDate }).concat(...coverageConditionGroups({ policy, rows: answered }))
      .forEach((f) => { if (f.value != null) out[f.label] = f.value; });
    return out;
  }, [collapseAnswered, policy, answered, maxResetDate]);
  if (!policy) return null;

  const set = (field) => (v) => onChange(field, v && v.target ? v.target.value : v);
  const hintOf = (field) => hints[field] || (extraHints || {})[field];
  const fold = (label) => ({
    done: collapseAnswered && !opened[label] ? words[label] || null : null,
    onOpen: () => setOpened((prev) => Object.assign({}, prev, { [label]: true })),
  });
  const id = (field) => `pe-${field}`;
  const text = (field, placeholder, label) => (
    <Input id={id(field)} value={draft[field]} onChange={set(field)} placeholder={placeholder} aria-label={label} />
  );
  const number = (field, placeholder, suffix, prefix) => (
    <Input id={id(field)} type="number" min={0} value={draft[field]} onChange={set(field)} placeholder={placeholder}
      suffix={suffix} prefix={prefix} />
  );
  const choice = (field, options, placeholder) => (
    <Select id={id(field)} value={draft[field] === "" || draft[field] == null ? undefined : draft[field]}
      onChange={(v) => set(field)(v == null ? "" : v)} options={options} allowClear placeholder={placeholder || "Ask"} />
  );
  const date = (field, placeholder) => (
    <DatePicker id={id(field)} value={asDay(draft[field])} onChange={(d) => set(field)(d ? d.format("YYYY-MM-DD") : "")}
      format="MMM D, YYYY" placeholder={placeholder} style={{ width: "100%" }} />
  );
  const pair = (a, b, kind) => <div className={kind ? `pe-pair ${kind}` : "pe-pair"}>{a}{b}</div>;
  const year = benefitYearOf(policy, maxResetDate);
  const molar = molarFillingsOf({ policy, rows: answered });
  // Answers the server filled automatically (the lines, the relationship, a
  // program's rules): kept in step until a person sets them here (then theirs wins).
  const fromLines = fromLinesLabels(policy);
  const linesNote = (label) => (fromLines.has(label)
    ? `Filled automatically from ${autoFillSource(policy, label)}. Change it to set it yourself.` : null);

  return (
    <>
    <section className="pe-sec" aria-label="Plan details">
      <div className="bs-sec-head"><h3 className="bs-h3">Plan details</h3></div>
      <div className="pe-grid">
        <div className="pe-group">
          <Field label="Benefit year" {...fold("Benefit year")} htmlFor={id("benefit_period_type")} hint={hintOf("benefit_period_type")} onUse={set("benefit_period_type")}
            note={year.range && policy.benefit_period && policy.benefit_period.rolled
              ? `The tab shows ${year.range}, rolled forward from these dates` : linesNote("Benefit year")}>
            <div className="pe-stack">
              {choice("benefit_period_type", optionsOf("benefit_period_type"), "Calendar or policy year")}
              {pair(date("benefit_year_start", "Starts"), date("benefit_year_end", "Ends"))}
            </div>
          </Field>
          <Field label="Deductible" {...fold("Deductible")} htmlFor={id("deductible")} hint={hintOf("deductible")} onUse={set("deductible")} note={linesNote("Deductible")}>{text("deductible", "As the carrier words it")}</Field>
        </div>
        <div className="pe-group">
          <Field label="Fee guide" {...fold("Fee guide")} htmlFor={id("fee_guide")} hint={hintOf("fee_guide")} onUse={set("fee_guide")}>{text("fee_guide", "e.g. 2026 Ontario")}</Field>
          <Field label="Maximums apply" {...fold("Maximums apply")} htmlFor={id("max_scope")} hint={hintOf("max_scope")} onUse={set("max_scope")}>
            {choice("max_scope", optionsOf("max_scope"))}
          </Field>
        </div>
        <div className="pe-group">
          <Field label="Pays the office directly" {...fold("Pays the office directly")} htmlFor={id("assignment_of_benefits")} hint={hintOf("assignment_of_benefits")} onUse={set("assignment_of_benefits")} note={linesNote("Pays the office directly")}>
            {choice("assignment_of_benefits", optionsOf("assignment_of_benefits"))}
          </Field>
          <Field label="Coverage ends" {...fold("Coverage ends")} htmlFor={id("age_limit")} hint={hintOf("age_limit")} onUse={set("age_limit")} note={linesNote("Coverage ends")}>
            {pair(number("age_limit", "At age", "yrs"), date("coverage_end_date", "Or on a date"))}
          </Field>
        </div>
      </div>
    </section>

    <section className="pe-sec" aria-label="Coverage conditions">
      <div className="bs-sec-head"><h3 className="bs-h3">Coverage conditions</h3></div>
      <div className="pe-grid">
        <div className="pe-group">
          <Field label="Alternate benefit" {...fold("Alternate benefit")} htmlFor={id("alternate_benefit")} hint={hintOf("alternate_benefit")} onUse={set("alternate_benefit")} note={linesNote("Alternate benefit")}>
            {pair(choice("alternate_benefit", optionsOf("alternate_benefit")), text("alternate_benefit_note", "Applies to"))}
          </Field>
          <Field label="White fillings on molars" {...fold("White fillings on molars")}
            note={molar.code ? `Answered on the ${molar.code} line` : "Answered on the white fillings on molars line"}>
            <div className={molar.words ? "pe-static" : "pe-static none"}>{molar.words || "Ask"}</div>
          </Field>
          <Field label="Waiting periods" {...fold("Waiting periods")} htmlFor={id("waiting_period_note")}>{text("waiting_period_note", "As the carrier words it")}</Field>
          <Field label="Specialist fees" {...fold("Specialist fees")} htmlFor={id("specialist_fee_covered")} hint={hintOf("specialist_fee_covered")} onUse={set("specialist_fee_covered")}>
            {pair(choice("specialist_fee_covered", SPECIALIST), text("specialist_fee_scope", "Which ones"))}
          </Field>
          <Field label="Missing tooth clause" {...fold("Missing tooth clause")} htmlFor={id("missing_tooth_clause")} hint={hintOf("missing_tooth_clause")} onUse={set("missing_tooth_clause")}>{choice("missing_tooth_clause", optionsOf("missing_tooth_clause"))}</Field>
        </div>
        <div className="pe-group">
          <Field label="Implants" {...fold("Implants")} htmlFor={id("implants_coverage")} hint={hintOf("implants_coverage")} onUse={set("implants_coverage")} note={linesNote("Implants")}>
            {pair(choice("implants_coverage", optionsOf("implants_coverage")), number("implants_percentage", "", "%"), "narrow")}
          </Field>
          <Field label="Perio & endo" {...fold("Perio & endo")} htmlFor={id("perio_endo_in_basic")} hint={hintOf("perio_endo_in_basic")} onUse={set("perio_endo_in_basic")} note={linesNote("Perio & endo")}>
            {choice("perio_endo_in_basic", PERIO_ENDO)}
          </Field>
          <Field label="Predetermination above" {...fold("Predetermination above")} htmlFor={id("predetermination_threshold")} hint={hintOf("predetermination_threshold")} onUse={set("predetermination_threshold")}>
            {number("predetermination_threshold", "Amount", undefined, "$")}
          </Field>
        </div>
        <div className="pe-group">
          <Field label="Single or family" {...fold("Single or family")} htmlFor={id("coverage_scope")} note={linesNote("Single or family")}>{choice("coverage_scope", optionsOf("coverage_scope"))}</Field>
          <Field label="Children covered to" {...fold("Children covered to")} htmlFor={id("dependent_age_limit")} hint={hintOf("dependent_age_limit")} onUse={set("dependent_age_limit")}>{number("dependent_age_limit", "Age", "yrs")}</Field>
          <Field label="Students covered to" {...fold("Students covered to")} htmlFor={id("student_age_limit")} hint={hintOf("student_age_limit")} onUse={set("student_age_limit")}>{number("student_age_limit", "Age", "yrs")}</Field>
        </div>
      </div>
    </section>
    </>
  );
}

export default function PlanEditPanel({ policy, patient, rows, carrierOptions, isLoadingCarriers, maxResetDate, breakdownId, onSaved, onFieldSaved, onCancel }) {
  // The server re-reads the plan from this breakdown's lines after a save.
  const withBreakdown = (payload) => (breakdownId ? Object.assign({ breakdown_id: breakdownId }, payload) : payload);
  const [draft, setDraft] = useState(() => valuesOf(policy, FIELDS));
  const [saving, setSaving] = useState(false);
  // The one Member/Patient row being edited, and its values.
  const [editing, setEditing] = useState(null);
  const [quick, setQuick] = useState({});
  const [quickSaving, setQuickSaving] = useState(false);

  // A row saved on its own brings back a new policy. Plan fields nobody has
  // touched follow it; the ones being filled in keep what was typed.
  const shown = useRef(policy);
  useEffect(() => {
    const before = shown.current;
    shown.current = policy;
    if (before === policy) return;
    setDraft((prev) => {
      const next = Object.assign({}, prev);
      Object.keys(FIELDS).forEach((f) => {
        if (sendValue(FIELDS[f], prev[f]) === sendValue(FIELDS[f], (before || {})[f])) {
          next[f] = policy && policy[f] != null ? policy[f] : "";
        }
      });
      return next;
    });
  }, [policy]);

  const changes = useMemo(() => changedFields(policy, draft), [policy, draft]);
  const dirty = Object.keys(changes).length > 0;
  if (!policy) return null;

  const set = (field) => (v) => setDraft((prev) => Object.assign({}, prev, { [field]: v && v.target ? v.target.value : v }));
  const id = (field) => `pe-${field}`;

  // ---- one row at a time (Member, Relationship) ----
  function startEdit(row) {
    setEditing(row.label);
    setQuick(valuesOf(policy, row.fields.reduce((o, f) => Object.assign(o, { [f]: IDENTITY[f] }), {})));
  }
  const stopEdit = () => { setEditing(null); setQuick({}); };
  async function saveRow(row) {
    const payload = {};
    row.fields.forEach((f) => {
      const after = sendValue(IDENTITY[f], quick[f]);
      if (after !== sendValue(IDENTITY[f], policy[f])) payload[f] = after;
    });
    if (!Object.keys(payload).length) { stopEdit(); return; }
    setQuickSaving(true);
    try {
      const res = await rootController.updatePatientInsurancePolicy(policy.id, withBreakdown(payload));
      if (res && res.status === 200) {
        message.success(`${row.label} saved`);
        const repoint = describeRepoint(res.json && res.json.mapping_repointed);
        if (repoint) message.info(repoint, 6);
        stopEdit();
        if (onFieldSaved) onFieldSaved(res.json || {});
      } else {
        message.error((res && res.json && res.json.error) || `Couldn't save the ${row.label.toLowerCase()}`);
      }
    } catch (err) {
      message.error(`Couldn't save the ${row.label.toLowerCase()}`);
    } finally {
      setQuickSaving(false);
    }
  }
  const setQ = (field) => (v) => setQuick((prev) => Object.assign({}, prev, { [field]: v && v.target ? v.target.value : v }));
  const qText = (field, placeholder, label, row) => (
    <Input id={id(field)} value={quick[field]} onChange={setQ(field)} placeholder={placeholder} aria-label={label}
      onPressEnter={() => saveRow(row)} autoFocus={row.fields[0] === field} />
  );
  const qChoice = (field, options, label, extraProps) => (
    <Select id={id(field)} value={quick[field] === "" || quick[field] == null ? undefined : quick[field]}
      onChange={(v) => setQ(field)(v == null ? "" : v)} options={options} aria-label={label} autoFocus defaultOpen {...extraProps} />
  );
  function editorOf(row) {
    switch (row.fields[0]) {
      case "first_name":
        return <div className="pe-pair">{qText("first_name", "First", "First name", row)}{qText("last_name", "Last", "Last name", row)}</div>;
      case "date_of_birth":
        return (
          <DatePicker id={id("date_of_birth")} value={asDay(quick.date_of_birth)} format="MMM D, YYYY" aria-label="Date of birth"
            onChange={(d) => setQ("date_of_birth")(d ? d.format("YYYY-MM-DD") : "")} placeholder="Pick a date" />
        );
      case "insurance_name":
        return qChoice("insurance_name", carrierOptions || [], "Carrier", {
          showSearch: true, loading: isLoadingCarriers,
          filterOption: (input, o) => String(o.label || "").toLowerCase().indexOf(input.toLowerCase()) >= 0,
        });
      case "insurance_type":
        return qChoice("insurance_type", ORDINALS, "Ordinal");
      case "relation_to_member":
        return qChoice("relation_to_member", RELATIONS, "Relationship", { allowClear: true });
      default:
        return qText(row.fields[0], "", row.label, row);
    }
  }
  const quickRow = (row, value) => (
    <QuickRow key={row.label} label={row.label} value={value} editable editing={editing === row.label}
      editor={editing === row.label ? editorOf(row) : null} saving={quickSaving}
      extra={editing === row.label && row.fields[0] === "date_of_birth" ? (
        <MemberDobSuggestion patientId={patient && patient.id} policyId={policy.id}
          first={policy.first_name} last={policy.last_name} active={!quick.date_of_birth}
          onUse={(dob) => setQ("date_of_birth")(dob)} />
      ) : null}
      onEdit={() => startEdit(row)} onSave={() => saveRow(row)} onCancel={stopEdit} />
  );
  const memberValues = memberFields(policy).reduce((o, f) => Object.assign(o, { [f.label]: f.value }), {});

  async function save() {
    if (!dirty) { onCancel(); return; }
    setSaving(true);
    try {
      const res = await rootController.updatePatientInsurancePolicy(policy.id, withBreakdown(changes));
      if (res && res.status === 200) {
        message.success("Changes saved");
        onSaved(res.json || {});
      } else {
        message.error((res && res.json && res.json.error) || "Couldn't save the plan");
      }
    } catch (err) {
      message.error("Couldn't save the plan");
    } finally {
      setSaving(false);
    }
  }

  const count = Object.keys(changes).length;

  return (
    <div className="pe bs-fields" data-testid="plan-edit-panel">
      <div className="pe-body">
        <div className="pe-top">
          <section className="pe-col" aria-label="Patient">
            <div className="bs-col-head"><h3 className="bs-h3">Patient</h3><span className="pe-from">from the PMS</span></div>
            <dl className="pe-quick">
              {patientFields(patient, policy).map((f) => (f.label === "Relationship"
                ? quickRow(RELATIONSHIP_ROW, f.value)
                : <QuickRow key={f.label} label={f.label} value={f.value} />))}
            </dl>
          </section>
          <section className="pe-col" aria-label="Member">
            <div className="bs-col-head"><h3 className="bs-h3">Member</h3></div>
            <dl className="pe-quick">
              {MEMBER_ROWS.map((row) => quickRow(row, memberValues[row.label]))}
            </dl>
          </section>
        </div>

        <PlanFieldsForm policy={policy} rows={rows} draft={draft} onChange={(field, v) => set(field)(v)} maxResetDate={maxResetDate} />
      </div>

      <div className="pe-foot">
        <span className="pe-status">
          {count ? <><b>{`${count} change${count === 1 ? "" : "s"}`}</b>{". What you save stays when the plan is fetched again."}</>
            : "Member and patient rows save on their own. Plan details and coverage conditions save here."}
        </span>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>Save changes</Button>
      </div>
    </div>
  );
}
